/**
 * DataStatusModal.ts
 * Glassmorphic modal displaying NWP weather model version telemetry,
 * cycle timestamps, and real-time UTC age calculations with health indicators.
 * Populates instantaneously from in-memory manifest with network fallback.
 */

import { store } from '../../state';

export interface ModelVersionItem {
  id: string;
  label: string;
  description: string;
  version: string;
  cycle_time_utc: string | null;
  cycle_display: string;
  thresholds?: {
    green_max_hours: number;
    yellow_max_hours: number;
  };
}

export interface ModelVersionsData {
  generated_at_utc: string;
  mode: string;
  thresholds: {
    green_max_hours: number;
    yellow_max_hours: number;
    model_thresholds?: Record<string, { green_max_hours?: number; yellow_max_hours?: number }>;
  };
  models: ModelVersionItem[];
}

export class DataStatusModal {
  private container: HTMLElement;
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'gw-status-modal';
    this.container.className = 'gw-about-modal-backdrop gw-data-status-backdrop';
    this.container.style.display = 'none';

    this.container.addEventListener('click', (e) => {
      if (e.target === this.container) {
        this.close();
      }
    });

    document.body.appendChild(this.container);
  }

  public close(): void {
    this.container.style.display = 'none';
    this.container.innerHTML = '';
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
  }

  public async open(): Promise<void> {
    this.close();
    this.container.style.display = 'flex';

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);

    // 1. Instant lookup from in-memory manifest (0ms population)
    const manifest = store.getState().manifest;
    if (manifest?.model_versions && Array.isArray(manifest.model_versions.models) && manifest.model_versions.models.length > 0) {
      this.renderContent(manifest.model_versions as ModelVersionsData);
      return;
    }

    // 2. Fallback to network fetch if manifest has not yet loaded model_versions
    this.renderLoading();

    try {
      const data = await this.fetchModelVersions();
      this.renderContent(data);
    } catch (err: any) {
      this.renderError(err?.message || 'Failed to load model status data.');
    }
  }

  private async fetchModelVersions(): Promise<ModelVersionsData> {
    const timestamp = Date.now();
    const manifest = store.getState().manifest;
    const isProductionHost = window.location.hostname === 'skyskout.com' ||
      window.location.hostname.endsWith('.skyskout.com') ||
      window.location.hostname === 'goldenwindow.app' ||
      window.location.hostname.endsWith('.goldenwindow.app');

    const candidateUrls: string[] = [];
    if (isProductionHost) {
      const apiServer = manifest?.api_server || 'https://auth.skyskout.com';
      candidateUrls.push(`${apiServer.replace(/\/$/, '')}/modelVersions.json?_t=${timestamp}`);
      candidateUrls.push(`modelVersions.json?_t=${timestamp}`);
    } else {
      candidateUrls.push(`tiles/modelVersions.json?_t=${timestamp}`);
      candidateUrls.push(`modelVersions.json?_t=${timestamp}`);
      candidateUrls.push(`/modelVersions.json?_t=${timestamp}`);
    }

    for (const url of candidateUrls) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 2500);
        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);
        if (res.ok) {
          const json = await res.json();
          if (json && Array.isArray(json.models)) {
            return json as ModelVersionsData;
          }
        }
      } catch {
        // Try next candidate URL
      }
    }

    throw new Error('Unable to retrieve weather model version metadata.');
  }

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="gw-about-dialog gw-data-status-dialog" role="dialog" aria-modal="true">
        <div class="gw-about-header">
          <div class="gw-about-title-group">
            <span class="gw-about-logo-icon">📡</span>
            <div>
              <h2 class="gw-about-title">Weather Model Data Status</h2>
              <span class="gw-about-subtitle">Numerical Weather Prediction Model Freshness & Telemetry</span>
            </div>
          </div>
          <button id="gw-status-close-x" class="gw-about-close" title="Close" aria-label="Close">&times;</button>
        </div>
        <div class="gw-data-status-loading">
          <div class="gw-data-status-spinner"></div>
          <p>Querying model cycle versions and server telemetry...</p>
        </div>
      </div>
    `;

    const closeBtn = document.getElementById('gw-status-close-x');
    if (closeBtn) closeBtn.onclick = () => this.close();
  }

  private renderError(message: string): void {
    this.container.innerHTML = `
      <div class="gw-about-dialog gw-data-status-dialog" role="dialog" aria-modal="true">
        <div class="gw-about-header">
          <div class="gw-about-title-group">
            <span class="gw-about-logo-icon">📡</span>
            <div>
              <h2 class="gw-about-title">Weather Model Data Status</h2>
              <span class="gw-about-subtitle">Numerical Weather Prediction Model Freshness & Telemetry</span>
            </div>
          </div>
          <button id="gw-status-close-x" class="gw-about-close" title="Close" aria-label="Close">&times;</button>
        </div>
        <div class="gw-data-status-error">
          <span class="gw-data-status-error-icon">⚠️</span>
          <h3>Telemetry Unavailable</h3>
          <p>${message}</p>
        </div>
        <div class="gw-about-footer">
          <button id="gw-status-close-btn" class="gw-about-ok-btn">Close</button>
        </div>
      </div>
    `;

    const closeX = document.getElementById('gw-status-close-x');
    if (closeX) closeX.onclick = () => this.close();
    const closeBtn = document.getElementById('gw-status-close-btn');
    if (closeBtn) closeBtn.onclick = () => this.close();
  }

  private calculateAgeHours(cycleTimeUtc: string | null): number | null {
    if (!cycleTimeUtc) return null;
    const cycleMs = Date.parse(cycleTimeUtc);
    if (isNaN(cycleMs)) return null;
    const nowMs = Date.now();
    const diffHours = (nowMs - cycleMs) / (1000 * 60 * 60);
    return diffHours >= 0 ? diffHours : 0;
  }

  private getModelIcon(modelId: string): string {
    switch (modelId.toLowerCase()) {
      case 'hrrr': return '🇺🇸';
      case 'rrfs': return '🇺🇸';
      case 'gfs': return '🇺🇸';
      case 'gefs': return '🇺🇸';
      case 'nbm': return '🇺🇸';
      case 'rdps': return '🇨🇦';
      case 'gdps': return '🇨🇦';
      case 'raqdps': return '🇨🇦';
      case 'ecmwf': return '🇪🇺';
      case 'icon13': return '🇩🇪';
      default: return '🌤️';
    }
  }

  private renderContent(data: ModelVersionsData): void {
    const defaultGreenMax = data.thresholds?.green_max_hours ?? 12;
    const defaultYellowMax = data.thresholds?.yellow_max_hours ?? 18;

    const cardsHtml = data.models.map((m) => {
      const ageHours = this.calculateAgeHours(m.cycle_time_utc);
      
      // Resolve per-model threshold overrides (e.g. ECMWF 18h green, 24h yellow)
      const greenMax = m.thresholds?.green_max_hours ??
        data.thresholds?.model_thresholds?.[m.id]?.green_max_hours ??
        defaultGreenMax;
      const yellowMax = m.thresholds?.yellow_max_hours ??
        data.thresholds?.model_thresholds?.[m.id]?.yellow_max_hours ??
        defaultYellowMax;

      let statusClass = 'gw-status-red';
      let statusLabel = 'Out of Date';

      if (ageHours === null || m.version === 'Unknown') {
        statusClass = 'gw-status-red';
        statusLabel = 'Missing';
      } else if (ageHours < greenMax) {
        statusClass = 'gw-status-green';
        statusLabel = 'Up to Date';
      } else if (ageHours < yellowMax) {
        statusClass = 'gw-status-yellow';
        statusLabel = 'Aging';
      } else {
        statusClass = 'gw-status-red';
        statusLabel = 'Out of Date';
      }

      const icon = this.getModelIcon(m.id);

      return `
        <div class="gw-status-model-card">
          <div class="gw-status-card-main">
            <div class="gw-status-card-header">
              <span class="gw-status-model-icon">${icon}</span>
              <div>
                <h4 class="gw-status-model-name">${m.label}</h4>
                <p class="gw-status-model-desc">${m.description}</p>
              </div>
            </div>
          </div>
          <div class="gw-status-card-indicator">
            <div class="gw-status-pill ${statusClass}">
              <span class="gw-status-dot"></span>
              <span class="gw-status-pill-label">${statusLabel}</span>
            </div>
          </div>
        </div>
      `;
    }).join('');

    this.container.innerHTML = `
      <div class="gw-about-dialog gw-data-status-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-status-modal-title">
        <div class="gw-about-header">
          <div class="gw-about-title-group">
            <span class="gw-about-logo-icon">📡</span>
            <div>
              <h2 id="gw-status-modal-title" class="gw-about-title">Weather Model Data Status</h2>
              <span class="gw-about-subtitle">Numerical Weather Prediction Model Freshness</span>
            </div>
          </div>
          <button id="gw-status-close-x" class="gw-about-close" title="Close" aria-label="Close">&times;</button>
        </div>

        <div class="gw-about-body gw-data-status-body">
          <!-- Models List -->
          <div class="gw-status-models-list">
            ${cardsHtml}
          </div>
        </div>

        <div class="gw-about-footer">
          <div class="gw-status-footer-meta">
            <span>Instant telemetry from active manifest</span>
          </div>
          <button id="gw-status-refresh-btn" class="gw-about-ok-btn gw-status-refresh-btn" style="margin-right: 8px;">↻ Refresh</button>
          <button id="gw-status-ok-btn" class="gw-about-ok-btn">Close</button>
        </div>
      </div>
    `;

    const closeX = document.getElementById('gw-status-close-x');
    if (closeX) closeX.onclick = () => this.close();

    const okBtn = document.getElementById('gw-status-ok-btn');
    if (okBtn) okBtn.onclick = () => this.close();

    const refreshBtn = document.getElementById('gw-status-refresh-btn');
    if (refreshBtn) {
      refreshBtn.onclick = async () => {
        refreshBtn.innerText = 'Refreshing...';
        try {
          const fresh = await this.fetchModelVersions();
          this.renderContent(fresh);
        } catch {
          refreshBtn.innerText = 'Failed';
          setTimeout(() => {
            if (refreshBtn) refreshBtn.innerText = '↻ Refresh';
          }, 1500);
        }
      };
    }
  }
}

export default DataStatusModal;
